import { OnlineStatusEnum } from './enum/onlineStatus.enum';
import { PRESENCE_STALE_SECONDS } from './auctionPresence.const';
import type { HeartbeatsMap, TabVisibleMap } from '../_hubs/auctionUserAdmin.hub';

export function secondsSince(utcMs: number): number {
    return Math.max(0, Math.floor((Date.now() - utcMs) / 1000));
}

export function getUserPresence(params: {
    userId: string;
    onlineUsers?: string[];
    lastSeenByUserId?: HeartbeatsMap;
    tabVisibleByUserId?: TabVisibleMap;
}): OnlineStatusEnum {
    const socketConnected = params.onlineUsers?.some((u) => u === params.userId) ?? false;
    if (!socketConnected) return OnlineStatusEnum.Offline;

    const utcMs = params.lastSeenByUserId?.[params.userId];
    const heartbeatStale = utcMs == null || secondsSince(utcMs) > PRESENCE_STALE_SECONDS;
    const tabVisible = params.tabVisibleByUserId?.[params.userId] ?? true;

    if (heartbeatStale || !tabVisible) return OnlineStatusEnum.Away;
    return OnlineStatusEnum.Online;
}

export function presenceLabel(status: OnlineStatusEnum): string {
    if (status === OnlineStatusEnum.Online) return 'Online';
    if (status === OnlineStatusEnum.Away) return 'Away';
    return 'Offline';
}

export function summarizeRegisteredUserPresence(users: { userId: string }[], params: {
    onlineUsers?: string[];
    lastSeenByUserId?: HeartbeatsMap;
    tabVisibleByUserId?: TabVisibleMap;
}): { message: string; severity: 'success' | 'warning' | 'error' } {
    if (!users.length) {
        return { message: 'No registered users on this auction', severity: 'warning' };
    }

    let awayCount = 0;
    let offlineCount = 0;

    users.forEach((user) => {
        const presence = getUserPresence({
            userId: user.userId,
            onlineUsers: params.onlineUsers,
            lastSeenByUserId: params.lastSeenByUserId,
            tabVisibleByUserId: params.tabVisibleByUserId,
        });
        if (presence === OnlineStatusEnum.Offline) offlineCount += 1;
        else if (presence === OnlineStatusEnum.Away) awayCount += 1;
    });

    if (awayCount === 0 && offlineCount === 0) {
        return { message: 'All users are online', severity: 'success' };
    }

    const parts: string[] = [];
    if (offlineCount > 0) {
        parts.push(`${offlineCount} user${offlineCount === 1 ? '' : 's'} offline`);
    }
    if (awayCount > 0) {
        parts.push(`${awayCount} user${awayCount === 1 ? '' : 's'} away`);
    }

    return {
        message: parts.join(', '),
        severity: offlineCount > 0 ? 'error' : 'warning',
    };
}
